import React from "react";
import { NavLink } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import {
  useFetch,
  useFilter,
  useLanguage,
  usePagination,
  useUrlParams,
} from "hooks";
import {
  BookCard,
  GridList,
  Layout,
  SearchForm,
  Icons,
  Skeletons,
  Skeleton,
} from "components";
import SearchSideFilter from "components/SearchSideFilter";
import qs from "qs";

export default function Normal() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const { page } = usePagination();
  const [booksFilterResponse, booksFilterRequest] = useFetch(
    `getBooksFilters`,
    "POST"
  );
  const [booksResponse, booksRequest] = useFetch(`getBooks`, "POST");
  const [searchKey, setSearchKey] = React.useState("");
  const filters = useFilter(booksFilterResponse?.result?.data);
  const { params, addParams, removeParams } = useUrlParams();

  const handleGetData = React.useCallback(async () => {
    // handle get books data by the filters from the url search query
    let myFilters = qs.parse(params);

    let restFilters = { ...myFilters };
    if (restFilters.date) {
      delete restFilters.date;
    }
    if (restFilters.filters) {
      delete restFilters.filters;
    }
    booksRequest(
      {
        ...restFilters,
        ...(myFilters.date && {
          datefrom_key: myFilters.date.from,
          dateto_key: myFilters.date.to,
        }),
        filters: {
          ...myFilters.filters,
        },
        search_key: searchKey,
      },
      `?page=${page}`
    );
  }, [booksRequest, page, searchKey, params]);

  React.useEffect(() => {
    // handle get books filters data by the filters from the url search query
    let myFilters = qs.parse(params);
    let restFilters = { ...myFilters };
    if (restFilters.date) delete restFilters.date;
    if (restFilters.filters) delete restFilters.filters;

    booksFilterRequest({
      ...restFilters,
      ...(myFilters.date && {
        datefrom_key: myFilters.date.from,
        dateto_key: myFilters.date.to,
      }),
      filters: {
        ...myFilters.filters,
      },
      search_key: searchKey,
      page_type: "search",
      refresh_total: 1,
    });
  }, [booksFilterRequest, params, searchKey]);

  React.useEffect(() => {
    handleGetData();
  }, [filters.selectedFilters, handleGetData, page]);

  const renderSelectedFilters = () => {
    // handle render selected books filters by the filters custom hook

    let filterParams =
      typeof qs.parse(params).filters !== "undefined"
        ? qs.parse(params)
        : { filters: {} };
    return (
      <ul className="d-flex flex-wrap gap-4 list-unstyled mb-5">
        {filters
          .getAllFilters()
          ?.filter(
            ({ key }) => filterParams?.filters && filterParams?.filters[key]
          )
          .map((item, index) => {
            const values = item.values?.filter(({ id }) =>
              filterParams.filters[item.key].includes(String(id))
            );

            if (values.length > 0)
              return (
                <li key={index} className="d-flex flex-wrap align-items-center">
                  <h5 className="text-primary ms-1">{item.name}: </h5>
                  <ul className="d-flex flex-wrap gap-2 list-unstyled">
                    {values.map((value, index) => (
                      <li
                        key={index}
                        className="btn bg-blue text-white px-2"
                        onClick={() =>
                          removeParams({
                            filters: {
                              [item.key]: [value.id],
                            },
                          })
                        }
                      >
                        <small>{value.name}</small> <Icons.Cancel />
                      </li>
                    ))}
                  </ul>
                </li>
              );
            return <></>;
          })}
      </ul>
    );
  };

  return (
    <Layout>
      <div id="search_page" className="page_container">
        <section>
          <Container>
            <Row className="mb-4 align-items-center">
              <Col xl={12} lg={12} md={9} sm={9} xs={9}>
                <h1 className="fw-medium text-primary">{language.search}</h1>
              </Col>
            </Row>
            <Row className="align-items-start justify-content-start">
              <Col
                md={12}
                sm={12}
                xs={12}
                className="order-1 d-block mb-4 pb-4"
              >
                <Row>
                  <Col xl={10}>
                    <Row className="align-items-center">
                      <Col xl={9} lg={9} xs={9}>
                        <SearchForm onSubmit={setSearchKey} />
                      </Col>
                      <Col
                        xl={3}
                        lg={3}
                        md={3}
                        sm={3}
                        xs={3}
                        className="px-1 px-sm-2"
                      >
                        <NavLink
                          to="/search/advanced"
                          className="btn btn-primary btn-outline-primary-hover w-100 px-1 px-sm-3"
                        >
                          {language.advanceSearch}
                        </NavLink>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>

              <Col
                xl={9}
                lg={8}
                md={12}
                sm={12}
                xs={12}
                className="order-2 order-lg-1"
              >
                <Row>
                  <Col xs={12}> {renderSelectedFilters()}</Col>

                  <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                    {!booksResponse.loading && booksResponse.result.data && (
                      <GridList
                        perRow={3}
                        itemsCount={booksResponse.result.meta.total}
                        perPage={booksResponse.result.meta.per_page}
                        as={BookCard}
                        items={booksResponse.result.data}
                      /> // book results card show
                    )}
                    {booksResponse.loading && (
                      <Skeletons.CardsSkeleton count={12} perRow={3} />
                    )}
                  </Col>
                </Row>
              </Col>

              <Col
                xl={3}
                lg={4}
                md={12}
                sm={12}
                xs={12}
                className="order-1 order-lg-2"
              >
                {!booksFilterResponse.loading ? (
                  booksFilterResponse.result.data && (
                    <SearchSideFilter
                      filters={filters}
                      addFilter={addParams}
                      removeFilter={removeParams}
                      filtersFromParams={
                        typeof qs.parse(params).filters !== "undefined"
                          ? qs.parse(params)
                          : { filters: {} }
                      }
                      type="checkbox"
                      CollapseProps={{
                        className: "collapse_list-2 svg-fill-black  mb-5",
                        titleClasses: "fw-bold",
                      }}
                    />
                    // component that displays filters and handel its actions by using useFilter
                  )
                ) : (
                  <Row className="g-4 ">
                    {Array(4)
                      .fill(null)
                      .map((_, index) => (
                        <Col xs={12} key={index} className="d-none d-lg-block">
                          <Skeleton count={1} height={300} width={"100%"} />
                        </Col>
                      ))}
                    <Col
                      xs={12}
                      className="d-flex justify-content-end  d-lg-none"
                    >
                      <Skeleton count={1} height={42} width={100} />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </Layout>
  );
}
